/**
 * Ticket Lifecycle v2 — Slice 5A.2
 *
 * Read-only hook fetching custody events for a single ticket. Powers
 * the per-ticket lifecycle time card (slice 5A.2) by providing the
 * input that `deriveTimeBuckets` needs for its pair walker.
 *
 * Separate from slice 4E's `useAdvisorMetrics` because:
 *   - That hook is shop-scoped + advisor-scoped and aggregates many
 *     tickets for a passport view.
 *   - This hook is single-ticket scoped for a detail surface.
 *   - Different query keys avoid cache thrashing.
 *
 * Boundary: imports only the lifecycle types + supabase client. No
 * scheduling / timer / parking / rollover / bay code.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { CustodyEventLite } from '@/lib/tickets/lifecycle/deriveTimeBuckets';
import type { CustodyEventKind } from '@/lib/tickets/lifecycle/types';

/** Only the event kinds the pair walker uses. Filtering at the DB
 *  reduces row volume; the walker is robust to extra kinds anyway. */
const TIMING_EVENT_KINDS: CustodyEventKind[] = [
  'kick_to_foh',
  'foh_accept',
  'kick_to_boh',
  'boh_accept',
  // Defensive: include reject_back so the open-interval rule can
  // correctly suppress trailing pending counts when an advisor refused
  // or a tech rejected the return.
  'reject_back',
];

export function useTicketCustodyEvents(
  ticketId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['ticket-custody-events', ticketId],
    enabled: enabled && !!ticketId,
    retry: false,
    queryFn: async (): Promise<CustodyEventLite[]> => {
      const { data, error } = await (supabase as any)
        .from('ticket_custody_events')
        .select('event_kind, ticket_id, created_at')
        .eq('ticket_id', ticketId)
        .in('event_kind', TIMING_EVENT_KINDS)
        .order('created_at', { ascending: true });
      if (error) {
        console.error(
          '[ticket-custody-events] failed to read — is the slice-2 ' +
            'migration applied to this Supabase project?',
          error,
        );
        throw error;
      }
      return ((data ?? []) as Array<{
        event_kind: string;
        ticket_id: string;
        created_at: string;
      }>).map((r) => ({
        event_kind: r.event_kind as CustodyEventKind,
        ticket_id: r.ticket_id,
        created_at: r.created_at,
      }));
    },
  });
}
